// ValidationModal.jsx
// Created: 2025-11-17
// Updated: 2025-11-17 - Reorganized with Static Validations, Business Rules, and Form Validations
// Purpose: Modal popup for selecting or creating validation templates and business rules

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import validationService from '../../../services/validationService';
import { createLogger } from '../../../utils/logger';

const logger = createLogger("ValidationModal");

// Resolve API base URL
const API_BASE_URL = 
  import.meta.env?.VITE_API_BASE || 
  process.env?.REACT_APP_API_BASE || 
  window.__API_BASE__ || 
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

// Static Validation Templates (17 types - no data collections required)
const VALIDATION_TYPES = {
  ALWAYS_SHOW: { label: 'Always Show', fields: [] },
  NEVER_SHOW: { label: 'Never Show', fields: [] },
  DATE_RANGE: { label: 'Date Range', fields: ['startDate', 'endDate'] },
  TIME_RANGE: { label: 'Time Range', fields: ['startTime', 'endTime'] },
  WEEKDAY_ONLY: { label: 'Weekday Only (Mon-Fri)', fields: [] },
  WEEKEND_ONLY: { label: 'Weekend Only (Sat-Sun)', fields: [] },
  MOBILE_DEVICE: { label: 'Mobile Device', fields: [] },
  DESKTOP_DEVICE: { label: 'Desktop Device', fields: [] },
  SPECIFIC_DAY: { label: 'Specific Day', fields: ['day'] },
  BEFORE_DATE: { label: 'Before Date', fields: ['cutoffDate'] },
  AFTER_DATE: { label: 'After Date', fields: ['startDate'] },
  BUSINESS_HOURS: { label: 'Business Hours (Mon-Fri 9AM-5PM)', fields: [] },
  SPECIFIC_MONTH: { label: 'Specific Month', fields: ['month'] },
  SEASON: { label: 'Season', fields: ['season'] },
  BROWSER_TYPE: { label: 'Browser Type', fields: ['browser'] },
  SCREEN_WIDTH: { label: 'Screen Width', fields: ['minWidth', 'maxWidth'] },
  FEATURE_FLAG: { label: 'Feature Flag', fields: ['flagName', 'enabled'] }
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SEASONS = ['Spring', 'Summer', 'Fall', 'Winter'];
const BROWSERS = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera'];

const RULE_CATEGORIES = [
  { id: 'STATIC', name: 'Static Rules', icon: 'âš¡' },
  { id: 'AUTHORIZATION', name: 'Authorization', icon: 'ðŸ”' },
  { id: 'VALIDATION', name: 'Validation', icon: 'âœ…' },
  { id: 'BUSINESS_RULE', name: 'Business Rule', icon: 'ðŸ’¼' }
];

const OPERATORS = {
  string: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'IN'],
  number: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'GREATER_OR_EQUAL', 'LESS_OR_EQUAL', 'BETWEEN'],
  boolean: ['IS_TRUE', 'IS_FALSE']
};

const ValidationModal = ({ isOpen, onClose, onApply, componentType }) => {
  const { appName } = useParams();
  const dbName = "jayram";

  // Accordion states
  const [expandedSection, setExpandedSection] = useState('static'); // 'static', 'business', 'form'
  
  // Data states
  const [staticTemplates, setStaticTemplates] = useState([]);
  const [businessRules, setBusinessRules] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(false);

  // Selection states
  const [selectedValidationId, setSelectedValidationId] = useState('');
  const [selectedType, setSelectedType] = useState('static'); // 'static' or 'business'

  // Create Static Template states
  const [validationName, setValidationName] = useState('');
  const [validationType, setValidationType] = useState('ALWAYS_SHOW');
  const [validationConfig, setValidationConfig] = useState({});

  // Create Business Rule states
  const [ruleName, setRuleName] = useState('');
  const [ruleCategory, setRuleCategory] = useState('AUTHORIZATION');
  const [ruleDescription, setRuleDescription] = useState('');
  const [conditions, setConditions] = useState([
    { id: 1, logicalOperator: 'IF', collection: '', field: '', operator: '', value: '' }
  ]);

  // Preview state
  const [previewResult, setPreviewResult] = useState(null);

  // Load validations and collections on mount
  useEffect(() => {
    if (isOpen && appName) {
      loadValidations();
      loadCollections();
    }
  }, [isOpen, appName]);

  const loadValidations = async () => {
    setLoading(true);
    try {
      logger.info("loadValidations", "Fetching validations", { appName });
      const data = await validationService.getAllValidations(appName, dbName);
      
      // Separate static templates from business rules
      const templates = data.filter(v => v.validationType || v.type === 'template');
      const rules = data.filter(v => v.ruleCategory && !v.validationType && v.type !== 'template');
      
      setStaticTemplates(templates);
      setBusinessRules(rules);
      logger.info("loadValidations", "Loaded validations", { templates: templates.length, rules: rules.length });
    } catch (error) {
      logger.error("loadValidations", "Failed to load validations", error);
      setStaticTemplates([]);
      setBusinessRules([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCollections = async () => {
    setLoadingCollections(true);
    try {
      logger.info("loadCollections", "Fetching collections", { appName });
      
      const commonCollections = ['users', 'transactions', 'orders', 'products', 'customers'];
      const foundCollections = [];

      for (const collType of commonCollections) {
        try {
          const url = `${API_BASE_URL}/api/collection/${encodeURIComponent(dbName)}/${encodeURIComponent(appName)}/${encodeURIComponent(collType)}`;
          const response = await fetch(url, { headers: { "Content-Type": "application/json" } });
          
          if (response.ok) {
            const data = await response.json();
            if (data && data.length > 0) {
              const firstDoc = data[0];
              const fields = Object.keys(firstDoc)
                .filter(key => key !== 'id' && key !== '_id')
                .map(key => ({
                  id: key,
                  name: formatFieldName(key),
                  type: inferFieldType(firstDoc[key])
                }));

              foundCollections.push({
                id: collType,
                name: formatCollectionName(collType),
                fields: fields
              });
            }
          }
        } catch (err) {
          logger.debug("loadCollections", `Collection ${collType} not found`);
        }
      }

      setCollections(foundCollections);
      logger.info("loadCollections", "Collections loaded", { count: foundCollections.length });
    } catch (error) {
      logger.error("loadCollections", "Failed to load collections", error);
      setCollections([]);
    } finally {
      setLoadingCollections(false);
    }
  };

  // Helper functions
  const formatFieldName = (fieldId) => {
    return fieldId.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).trim();
  };

  const formatCollectionName = (collId) => {
    return collId.charAt(0).toUpperCase() + collId.slice(1);
  };

  const inferFieldType = (value) => {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    return 'string';
  };

  const getFieldsByCollection = (collectionId) => {
    const collection = collections.find(c => c.id === collectionId);
    return collection ? collection.fields : [];
  };

  const getFieldType = (collectionId, fieldId) => {
    const fields = getFieldsByCollection(collectionId);
    const field = fields.find(f => f.id === fieldId);
    return field ? field.type : 'string';
  };

  // Business Rule functions
  const addCondition = () => {
    const newCondition = {
      id: conditions.length + 1,
      logicalOperator: 'AND',
      collection: '',
      field: '',
      operator: '',
      value: ''
    };
    setConditions([...conditions, newCondition]);
  };

  const updateCondition = (id, updates) => {
    setConditions(conditions.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  const removeCondition = (id) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter(c => c.id !== id));
    }
  };

  // Apply existing validation
  const handleApplyExisting = () => {
    const validation = selectedType === 'static'
      ? staticTemplates.find(v => v.id === selectedValidationId)
      : businessRules.find(v => v.id === selectedValidationId);

    if (!validation) {
      alert('Please select a validation');
      return;
    }

    onApply({
      validationId: validation.id,
      validationName: validation.name || validation.ruleName,
      validationType: validation.validationType || validation.type,
      validationConfig: validation.validationConfig || validation.config || {},
      ruleCategory: validation.ruleCategory,
      conditions: validation.conditions
    });
    onClose();
  };

  // Save and apply static template
  const handleSaveStaticTemplate = async () => {
    if (!validationName.trim()) {
      alert('Please enter a validation name');
      return;
    }

    try {
      setLoading(true);
      logger.info("handleSaveStaticTemplate", "Creating template", { name: validationName });

      const templateData = {
        id: `TEMPLATE_${Date.now()}`,
        name: validationName,
        ruleName: validationName,
        type: 'template',
        validationType: validationType,
        validationConfig: validationConfig,
        config: validationConfig,
        ruleCategory: 'VALIDATION',
        description: `Template validation: ${VALIDATION_TYPES[validationType]?.label}`,
        conditions: [],
        status: 'ACTIVE',
        createdAt: new Date().toISOString(),
        appName: appName
      };

      await validationService.createValidation(appName, templateData, dbName);
      
      onApply({
        validationId: templateData.id,
        validationName: templateData.name,
        validationType: templateData.validationType,
        validationConfig: templateData.validationConfig
      });

      alert('âœ… Template created and applied successfully!');
      onClose();
    } catch (error) {
      logger.error("handleSaveStaticTemplate", "Failed to create template", error);
      alert(`âŒ Failed to create template: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Save and apply business rule
  const handleSaveBusinessRule = async () => {
    if (!ruleName.trim()) {
      alert('Please enter a rule name');
      return;
    }

    try {
      setLoading(true);
      logger.info("handleSaveBusinessRule", "Creating rule", { name: ruleName });

      const ruleData = {
        id: `RULE_${Date.now()}`,
        name: ruleName,
        ruleName: ruleName,
        type: 'business_rule',
        ruleCategory: ruleCategory,
        description: ruleDescription,
        conditions: conditions,
        status: 'ACTIVE',
        createdAt: new Date().toISOString(),
        appName: appName
      };

      await validationService.createValidation(appName, ruleData, dbName);
      
      onApply({
        validationId: ruleData.id,
        validationName: ruleData.name,
        ruleCategory: ruleData.ruleCategory,
        conditions: ruleData.conditions
      });

      alert('âœ… Business rule created and applied successfully!');
      onClose();
    } catch (error) {
      logger.error("handleSaveBusinessRule", "Failed to create rule", error);
      alert(`âŒ Failed to create rule: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle validation type change
  const handleTypeChange = (type) => {
    setValidationType(type);
    setValidationConfig({});
    setPreviewResult(null);
  };

  // Handle config field change
  const handleConfigChange = (field, value) => {
    setValidationConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Preview validation (for static templates only)
  const handlePreview = () => {
    const validation = { type: validationType, config: validationConfig };
    const result = evaluateValidation(validation);
    setPreviewResult(result);
  };

  // Evaluate validation (same logic as before)
  const evaluateValidation = (validation) => {
    const now = new Date();
    const { type, config } = validation;

    switch (type) {
      case 'ALWAYS_SHOW':
        return { result: true, message: 'Always shows - condition met' };
      case 'NEVER_SHOW':
        return { result: false, message: 'Never shows - condition not met' };
      case 'DATE_RANGE':
        if (!config.startDate || !config.endDate) return { result: false, message: 'Missing date configuration' };
        const start = new Date(config.startDate);
        const end = new Date(config.endDate);
        const inRange = now >= start && now <= end;
        return { result: inRange, message: inRange ? 'Current date is within range' : 'Current date is outside range' };
      case 'TIME_RANGE':
        if (!config.startTime || !config.endTime) return { result: false, message: 'Missing time configuration' };
        const currentTime = now.getHours() * 60 + now.getMinutes();
        const [startH, startM] = config.startTime.split(':').map(Number);
        const [endH, endM] = config.endTime.split(':').map(Number);
        const startMin = startH * 60 + startM;
        const endMin = endH * 60 + endM;
        const inTimeRange = currentTime >= startMin && currentTime <= endMin;
        return { result: inTimeRange, message: inTimeRange ? 'Current time is within range' : 'Current time is outside range' };
      case 'WEEKDAY_ONLY':
        const isWeekday = now.getDay() >= 1 && now.getDay() <= 5;
        return { result: isWeekday, message: isWeekday ? 'Today is a weekday' : 'Today is not a weekday' };
      case 'WEEKEND_ONLY':
        const isWeekend = now.getDay() === 0 || now.getDay() === 6;
        return { result: isWeekend, message: isWeekend ? 'Today is weekend' : 'Today is not weekend' };
      case 'MOBILE_DEVICE':
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        return { result: isMobile, message: isMobile ? 'Mobile device detected' : 'Not a mobile device' };
      case 'DESKTOP_DEVICE':
        const isDesktop = !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        return { result: isDesktop, message: isDesktop ? 'Desktop device detected' : 'Not a desktop device' };
      case 'SPECIFIC_DAY':
        if (!config.day) return { result: false, message: 'No day configured' };
        const currentDay = DAYS[now.getDay() === 0 ? 6 : now.getDay() - 1];
        const matchesDay = currentDay === config.day;
        return { result: matchesDay, message: matchesDay ? `Today is ${config.day}` : `Today is not ${config.day}` };
      case 'BEFORE_DATE':
        if (!config.cutoffDate) return { result: false, message: 'No cutoff date configured' };
        const cutoff = new Date(config.cutoffDate);
        const beforeDate = now < cutoff;
        return { result: beforeDate, message: beforeDate ? 'Current date is before cutoff' : 'Current date is after cutoff' };
      case 'AFTER_DATE':
        if (!config.startDate) return { result: false, message: 'No start date configured' };
        const afterStart = new Date(config.startDate);
        const afterDate = now > afterStart;
        return { result: afterDate, message: afterDate ? 'Current date is after start date' : 'Current date is before start date' };
      case 'BUSINESS_HOURS':
        const day = now.getDay();
        const hour = now.getHours();
        const isBusinessHours = day >= 1 && day <= 5 && hour >= 9 && hour < 17;
        return { result: isBusinessHours, message: isBusinessHours ? 'Within business hours' : 'Outside business hours' };
      case 'SPECIFIC_MONTH':
        if (!config.month) return { result: false, message: 'No month configured' };
        const currentMonth = MONTHS[now.getMonth()];
        const matchesMonth = currentMonth === config.month;
        return { result: matchesMonth, message: matchesMonth ? `Current month is ${config.month}` : `Current month is not ${config.month}` };
      case 'SEASON':
        if (!config.season) return { result: false, message: 'No season configured' };
        const month = now.getMonth();
        let currentSeason = '';
        if (month >= 2 && month <= 4) currentSeason = 'Spring';
        else if (month >= 5 && month <= 7) currentSeason = 'Summer';
        else if (month >= 8 && month <= 10) currentSeason = 'Fall';
        else currentSeason = 'Winter';
        const matchesSeason = currentSeason === config.season;
        return { result: matchesSeason, message: matchesSeason ? `Current season is ${config.season}` : `Current season is ${currentSeason}, not ${config.season}` };
      case 'BROWSER_TYPE':
        if (!config.browser) return { result: false, message: 'No browser configured' };
        const userAgent = navigator.userAgent;
        let detectedBrowser = '';
        if (userAgent.includes('Chrome') && !userAgent.includes('Edge')) detectedBrowser = 'Chrome';
        else if (userAgent.includes('Firefox')) detectedBrowser = 'Firefox';
        else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) detectedBrowser = 'Safari';
        else if (userAgent.includes('Edge')) detectedBrowser = 'Edge';
        else if (userAgent.includes('Opera')) detectedBrowser = 'Opera';
        const matchesBrowser = detectedBrowser === config.browser;
        return { result: matchesBrowser, message: matchesBrowser ? `Browser is ${config.browser}` : `Browser is ${detectedBrowser}, not ${config.browser}` };
      case 'SCREEN_WIDTH':
        const width = window.innerWidth;
        const minOk = !config.minWidth || width >= parseInt(config.minWidth);
        const maxOk = !config.maxWidth || width <= parseInt(config.maxWidth);
        const widthOk = minOk && maxOk;
        return { result: widthOk, message: widthOk ? `Screen width ${width}px is within range` : `Screen width ${width}px is outside range` };
      case 'FEATURE_FLAG':
        if (!config.flagName) return { result: false, message: 'No flag name configured' };
        const flagEnabled = config.enabled === 'true' || config.enabled === true;
        return { result: flagEnabled, message: flagEnabled ? `Feature flag "${config.flagName}" is enabled` : `Feature flag "${config.flagName}" is disabled` };
      default:
        return { result: false, message: 'Unknown validation type' };
    }
  };

  // Render config fields for static templates
  const renderConfigFields = () => {
    const fields = VALIDATION_TYPES[validationType]?.fields || [];
    
    if (fields.length === 0) {
      return <p style={{ fontSize: '12px', color: '#6B7280', margin: '8px 0' }}>No additional configuration required</p>;
    }

    return fields.map(field => {
      switch (field) {
        case 'startDate':
        case 'endDate':
        case 'cutoffDate':
          return (
            <div key={field} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                {field.replace(/([A-Z])/g, ' $1').trim()}
              </label>
              <input
                type="date"
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}
              />
            </div>
          );
        case 'startTime':
        case 'endTime':
          return (
            <div key={field} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                {field.replace(/([A-Z])/g, ' $1').trim()}
              </label>
              <input
                type="time"
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}
              />
            </div>
          );
        case 'day':
          return (
            <div key={field} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Day of Week</label>
              <select
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}
              >
                <option value="">Select Day</option>
                {DAYS.map(day => <option key={day} value={day}>{day}</option>)}
              </select>
            </div>
          );
        case 'month':
          return (
            <div key={field} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Month</label>
              <select
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}
              >
                <option value="">Select Month</option>
                {MONTHS.map(month => <option key={month} value={month}>{month}</option>)}
              </select>
            </div>
          );
        case 'season':
          return (
            <div key={field} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Season</label>
              <select
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}
              >
                <option value="">Select Season</option>
                {SEASONS.map(season => <option key={season} value={season}>{season}</option>)}
              </select>
            </div>
          );
        case 'browser':
          return (
            <div key={field} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Browser</label>
              <select
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}
              >
                <option value="">Select Browser</option>
                {BROWSERS.map(browser => <option key={browser} value={browser}>{browser}</option>)}
              </select>
            </div>
          );
        case 'minWidth':
        case 'maxWidth':
          return (
            <div key={field} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                {field.replace(/([A-Z])/g, ' $1').trim()} (px)
              </label>
              <input
                type="number"
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}
                placeholder="e.g. 768"
              />
            </div>
          );
        case 'flagName':
          return (
            <div key={field} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '# 374151'}}>Feature Flag Name</label>
              <input
                type="text"
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}
                placeholder="e.g. new_feature_enabled"
              />
            </div>
          );
        case 'enabled':
          return (
            <div key={field} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Status</label>
              <select
                value={validationConfig[field] || 'false'}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '6px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '12px' }}
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
          );
        default:
          return null;
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '95%',
        maxWidth: '1100px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 1
        }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
              âœ… Add Validation to Component
            </h2>
            <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0 0' }}>
              Component: <span style={{ fontWeight: '600', color: '#3B82F6' }}>{componentType}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '6px 12px',
              fontSize: '14px',
              backgroundColor: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#6B7280'
            }}
          >
            âœ• Close
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              Loading...
            </div>
          ) : (
            <div>
              {/* ACCORDION 1: Static Validations */}
              <div style={{
                border: '2px solid #E5E7EB',
                borderRadius: '8px',
                marginBottom: '16px',
                overflow: 'hidden'
              }}>
                <div
                  onClick={() => setExpandedSection(expandedSection === 'static' ? '' : 'static')}
                  style={{
                    padding: '16px',
                    backgroundColor: expandedSection === 'static' ? '#EFF6FF' : '#F9FAFB',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: expandedSection === 'static' ? '1px solid #E5E7EB' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>âš¡</span>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                        Static Validations
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        17 pre-built templates (date, time, device, browser) â€¢ {staticTemplates.length} saved
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: '16px', color: '#6B7280' }}>
                    {expandedSection === 'static' ? 'â–¼' : 'â–¶'}
                  </span>
                </div>

                {expandedSection === 'static' && (
                  <div style={{ padding: '20px', backgroundColor: 'white' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      {/* Select Existing */}
                      <div>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                          ðŸ“š Select Existing Template
                        </h4>
                        <select
                          value={selectedType === 'static' ? selectedValidationId : ''}
                          onChange={(e) => {
                            setSelectedValidationId(e.target.value);
                            setSelectedType('static');
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px',
                            marginBottom: '12px'
                          }}
                        >
                          <option value="">Choose a template...</option>
                          {staticTemplates.map(v => (
                            <option key={v.id} value={v.id}>
                              {v.name || v.ruleName} - {VALIDATION_TYPES[v.validationType]?.label}
                            </option>
                          ))}
                        </select>

                        {staticTemplates.length === 0 && (
                          <div style={{
                            padding: '12px',
                            backgroundColor: '#FEF3C7',
                            border: '1px solid #FCD34D',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#92400E'
                          }}>
                            âš ï¸ No templates saved yet. Create one on the right â†’
                          </div>
                        )}
                      </div>

                      {/* Create New */}
                      <div>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                          âž• Create New Template
                        </h4>
                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                            Template Name
                          </label>
                          <input
                            type="text"
                            value={validationName}
                            onChange={(e) => setValidationName(e.target.value)}
                            placeholder="e.g., Show on Mobile Only"
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '12px'
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                            Validation Type
                          </label>
                          <select
                            value={validationType}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '12px'
                            }}
                          >
                            {Object.entries(VALIDATION_TYPES).map(([key, val]) => (
                              <option key={key} value={key}>{val.label}</option>
                            ))}
                          </select>
                        </div>

                        <div style={{
                          padding: '10px',
                          backgroundColor: '#F9FAFB',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          marginBottom: '10px',
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          {renderConfigFields()}
                        </div>

                        <button
                          onClick={handlePreview}
                          style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: '#8B5CF6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            marginBottom: '8px'
                          }}
                        >
                          ðŸ‘ï¸ Preview
                        </button>

                        {previewResult && (
                          <div style={{
                            padding: '10px',
                            borderRadius: '6px',
                            backgroundColor: previewResult.result ? '#D1FAE5' : '#FEE2E2',
                            border: `2px solid ${previewResult.result ? '#10B981' : '#EF4444'}`,
                            fontSize: '11px',
                            marginBottom: '8px'
                          }}>
                            <div style={{
                              fontWeight: '700',
                              color: previewResult.result ? '#065F46' : '#991B1B',
                              marginBottom: '4px'
                            }}>
                              {previewResult.result ? 'âœ… SHOW' : 'âŒ HIDE'}
                            </div>
                            <div style={{ color: previewResult.result ? '#065F46' : '#991B1B' }}>
                              {previewResult.message}
                            </div>
                          </div>
                        )}

                        <button
                          onClick={handleSaveStaticTemplate}
                          disabled={!validationName.trim()}
                          style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: !validationName.trim() ? '#D1D5DB' : '#10B981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: !validationName.trim() ? 'not-allowed' : 'pointer'
                          }}
                        >
                          ðŸ’¾ Save & Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ACCORDION 2: Business Rules */}
              <div style={{
                border: '2px solid #E5E7EB',
                borderRadius: '8px',
                marginBottom: '16px',
                overflow: 'hidden'
              }}>
                <div
                  onClick={() => setExpandedSection(expandedSection === 'business' ? '' : 'business')}
                  style={{
                    padding: '16px',
                    backgroundColor: expandedSection === 'business' ? '#EFF6FF' : '#F9FAFB',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: expandedSection === 'business' ? '1px solid #E5E7EB' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>ðŸ’¼</span>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                        Business Rules
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        Data-driven conditions based on collections â€¢ {businessRules.length} saved
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: '16px', color: '#6B7280' }}>
                    {expandedSection === 'business' ? 'â–¼' : 'â–¶'}
                  </span>
                </div>

                {expandedSection === 'business' && (
                  <div style={{ padding: '20px', backgroundColor: 'white' }}>
                    {/* No Collections Warning */}
                    {collections.length === 0 && !loadingCollections && (
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#FEF3C7',
                        border: '1px solid #FCD34D',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        fontSize: '13px',
                        color: '#92400E'
                      }}>
                        âš ï¸ <strong>No data collections present.</strong> Business rules require data collections like users, transactions, etc. Please create collections first.
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      {/* Select Existing */}
                      <div>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                          ðŸ“š Select Existing Rule
                        </h4>
                        <select
                          value={selectedType === 'business' ? selectedValidationId : ''}
                          onChange={(e) => {
                            setSelectedValidationId(e.target.value);
                            setSelectedType('business');
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        >
                          <option value="">Choose a business rule...</option>
                          {businessRules.map(r => (
                            <option key={r.id} value={r.id}>
                              {r.name || r.ruleName} - {r.ruleCategory}
                            </option>
                          ))}
                        </select>

                        {businessRules.length === 0 && (
                          <div style={{
                            padding: '12px',
                            backgroundColor: '#FEF3C7',
                            border: '1px solid #FCD34D',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#92400E',
                            marginTop: '12px'
                          }}>
                            âš ï¸ No business rules saved yet. Create one on the right â†’
                          </div>
                        )}

                        <div style={{
                          padding: '12px',
                          backgroundColor: '#F0F9FF',
                          border: '1px solid #BAE6FD',
                          borderRadius: '6px',
                          fontSize: '11px',
                          color: '#0369A1',
                          marginTop: '12px'
                        }}>
                          ðŸ’¡ <strong>Note:</strong> Business rule preview requires actual data and can only be tested with components.
                        </div>
                      </div>

                      {/* Create New */}
                      <div>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                          âž• Create New Business Rule
                        </h4>
                        
                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                            Rule Name
                          </label>
                          <input
                            type="text"
                            value={ruleName}
                            onChange={(e) => setRuleName(e.target.value)}
                            placeholder="e.g., Premium Customer Access"
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '12px'
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                            Category
                          </label>
                          <select
                            value={ruleCategory}
                            onChange={(e) => setRuleCategory(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '12px'
                            }}
                          >
                            {RULE_CATEGORIES.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                            ))}
                          </select>
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                            Description
                          </label>
                          <textarea
                            value={ruleDescription}
                            onChange={(e) => setRuleDescription(e.target.value)}
                            placeholder="Describe what this rule does..."
                            rows={2}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontFamily: 'inherit',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        {/* Conditions */}
                        <div style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          marginBottom: '10px',
                          padding: '10px',
                          backgroundColor: '#F9FAFB',
                          borderRadius: '6px',
                          border: '1px solid #E5E7EB'
                        }}>
                          {conditions.map((condition, index) => (
                            <div key={condition.id} style={{ marginBottom: '8px', fontSize: '11px' }}>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <select
                                  value={condition.logicalOperator}
                                  onChange={(e) => updateCondition(condition.id, { logicalOperator: e.target.value })}
                                  disabled={index === 0}
                                  style={{
                                    width: '60px',
                                    padding: '4px',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    fontWeight: '700'
                                  }}
                                >
                                  {index === 0 ? <option value="IF">IF</option> : (
                                    <>
                                      <option value="AND">AND</option>
                                      <option value="OR">OR</option>
                                    </>
                                  )}
                                </select>

                                <select
                                  value={condition.collection}
                                  onChange={(e) => updateCondition(condition.id, { collection: e.target.value, field: '', operator: '', value: '' })}
                                  disabled={collections.length === 0}
                                  style={{
                                    flex: 1,
                                    padding: '4px',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '4px',
                                    fontSize: '10px'
                                  }}
                                >
                                  <option value="">Table</option>
                                  {collections.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                  ))}
                                </select>

                                <select
                                  value={condition.field}
                                  onChange={(e) => updateCondition(condition.id, { field: e.target.value, operator: '', value: '' })}
                                  disabled={!condition.collection}
                                  style={{
                                    flex: 1,
                                    padding: '4px',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '4px',
                                    fontSize: '10px'
                                  }}
                                >
                                  <option value="">Field</option>
                                  {getFieldsByCollection(condition.collection).map(f => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                  ))}
                                </select>

                                <select
                                  value={condition.operator}
                                  onChange={(e) => updateCondition(condition.id, { operator: e.target.value })}
                                  disabled={!condition.field}
                                  style={{
                                    flex: 1,
                                    padding: '4px',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '4px',
                                    fontSize: '10px'
                                  }}
                                >
                                  <option value="">Op</option>
                                  {condition.field && OPERATORS[getFieldType(condition.collection, condition.field)]?.map(op => (
                                    <option key={op} value={op}>{op.replace(/_/g, ' ')}</option>
                                  ))}
                                </select>

                                <input
                                  type="text"
                                  value={condition.value}
                                  onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                                  placeholder="Value"
                                  style={{
                                    flex: 1,
                                    padding: '4px',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '4px',
                                    fontSize: '10px'
                                  }}
                                />

                                {conditions.length > 1 && (
                                  <button
                                    onClick={() => removeCondition(condition.id)}
                                    style={{
                                      padding: '2px 6px',
                                      backgroundColor: '#FEE2E2',
                                      border: '1px solid #FCA5A5',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '10px'
                                    }}
                                  >
                                    âœ•
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}

                          <button
                            onClick={addCondition}
                            disabled={collections.length === 0}
                            style={{
                              fontSize: '11px',
                              padding: '4px 8px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#3B82F6',
                              cursor: collections.length === 0 ? 'not-allowed' : 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            + Add Condition
                          </button>
                        </div>

                        <button
                          onClick={handleSaveBusinessRule}
                          disabled={!ruleName.trim() || collections.length === 0}
                          style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: (!ruleName.trim() || collections.length === 0) ? '#D1D5DB' : '#10B981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: (!ruleName.trim() || collections.length === 0) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          ðŸ’¾ Save & Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ACCORDION 3: Form Validations (Only visible if component is form) */}
              {componentType === 'form' && (
                <div style={{
                  border: '2px solid #E5E7EB',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <div
                    onClick={() => setExpandedSection(expandedSection === 'form' ? '' : 'form')}
                    style={{
                      padding: '16px',
                      backgroundColor: expandedSection === 'form' ? '#EFF6FF' : '#F9FAFB',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: expandedSection === 'form' ? '1px solid #E5E7EB' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '20px' }}>ðŸ“</span>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                          Form Validations
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                          Field-level validation rules for form components
                        </div>
                      </div>
                    </div>
                    <span style={{ fontSize: '16px', color: '#6B7280' }}>
                      {expandedSection === 'form' ? 'â–¼' : 'â–¶'}
                    </span>
                  </div>

                  {expandedSection === 'form' && (
                    <div style={{ padding: '40px 20px', backgroundColor: 'white', textAlign: 'center' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>ðŸš§</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Coming Soon
                      </div>
                      <div style={{ fontSize: '13px', color: '#6B7280' }}>
                        Form-specific validation rules are under development
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              backgroundColor: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#374151',
              fontWeight: '600'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleApplyExisting}
            disabled={!selectedValidationId}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              backgroundColor: !selectedValidationId ? '#D1D5DB' : '#10B981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: !selectedValidationId ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            âœ… Apply Selected
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidationModal;

